import { DialogComponent } from '@theme/dialog';

const STORAGE_KEY = 'myrabano:wishlist';

/**
 * @typedef {Object} WishlistItem
 * @property {string} handle
 * @property {string} title
 * @property {string} url
 * @property {string} image
 * @property {string} price
 */

/** @returns {WishlistItem[]} */
function readItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
}

/** @param {WishlistItem[]} items */
function writeItems(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  window.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items } }));
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

export const Wishlist = {
  /** @returns {WishlistItem[]} */
  getItems() {
    return readItems();
  },
  /** @param {string} handle */
  has(handle) {
    return readItems().some((item) => item.handle === handle);
  },
  /** @param {WishlistItem} item */
  add(item) {
    const items = readItems();
    if (!items.some((existing) => existing.handle === item.handle)) {
      items.push(item);
      writeItems(items);
    }
  },
  /** @param {string} handle */
  remove(handle) {
    writeItems(readItems().filter((item) => item.handle !== handle));
  },
  /**
   * @param {WishlistItem} item
   * @returns {boolean} whether the item is now saved (true) or removed (false)
   */
  toggle(item) {
    if (this.has(item.handle)) {
      this.remove(item.handle);
      return false;
    }
    this.add(item);
    return true;
  },
};

class WishlistDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.renderItems();
    this._onChange = () => this.renderItems();
    window.addEventListener('wishlist:change', this._onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('wishlist:change', this._onChange);
  }

  open() {
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  renderItems() {
    const items = Wishlist.getItems();
    const emptyEl = this.querySelector('[data-wishlist-empty]');
    const listEl = this.querySelector('[data-wishlist-list]');
    if (!emptyEl || !listEl) return;

    if (items.length === 0) {
      emptyEl.hidden = false;
      listEl.hidden = true;
      listEl.innerHTML = '';
      return;
    }

    emptyEl.hidden = true;
    listEl.hidden = false;
    listEl.innerHTML = items
      .map(
        (item) => `
          <li class="wishlist-drawer__item">
            <a href="${escapeHtml(item.url)}" class="wishlist-drawer__item-link">
              <img
                src="${escapeHtml(item.image)}"
                alt="${escapeHtml(item.title)}"
                class="wishlist-drawer__item-image"
                width="80"
                height="100"
                loading="lazy"
              >
              <span class="wishlist-drawer__item-details">
                <span class="wishlist-drawer__item-title">${escapeHtml(item.title)}</span>
                <span class="wishlist-drawer__item-price">${escapeHtml(item.price)}</span>
              </span>
            </a>
            <button
              type="button"
              class="wishlist-drawer__item-remove button-unstyled"
              data-wishlist-remove="${escapeHtml(item.handle)}"
              aria-label="Remove from wishlist"
            >
              &times;
            </button>
          </li>
        `
      )
      .join('');

    listEl.querySelectorAll('[data-wishlist-remove]').forEach((button) => {
      button.addEventListener('click', () => {
        const handle = button.getAttribute('data-wishlist-remove');
        if (handle) Wishlist.remove(handle);
      });
    });
  }
}

class WishlistIcon extends HTMLElement {
  connectedCallback() {
    this.updateCount();
    this._onChange = () => this.updateCount();
    window.addEventListener('wishlist:change', this._onChange);
  }

  disconnectedCallback() {
    window.removeEventListener('wishlist:change', this._onChange);
  }

  updateCount() {
    const count = Wishlist.getItems().length;
    const bubble = this.querySelector('[data-wishlist-count]');
    if (!bubble) return;
    bubble.textContent = count > 0 ? String(count) : '';
    this.classList.toggle('header-actions__wishlist-icon--has-items', count > 0);
  }
}

class WishlistToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.item = {
      handle: this.dataset.handle,
      title: this.dataset.title,
      url: this.dataset.url,
      image: this.dataset.image,
      price: this.dataset.price,
    };
    this.updateState();
    this.button?.addEventListener('click', this.handleClick);
    this._onChange = () => this.updateState();
    window.addEventListener('wishlist:change', this._onChange);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
    window.removeEventListener('wishlist:change', this._onChange);
  }

  handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const active = Wishlist.toggle(this.item);
    this.updateState(active);
    this.button?.classList.remove('product-card__wishlist-button--animate');
    void this.button?.offsetWidth;
    this.button?.classList.add('product-card__wishlist-button--animate');
  };

  updateState(activeOverride) {
    const active = activeOverride !== undefined ? activeOverride : Wishlist.has(this.item.handle);
    this.classList.toggle('wishlist-toggle--active', active);
    this.button?.setAttribute('aria-pressed', String(active));
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
if (!customElements.get('wishlist-icon')) {
  customElements.define('wishlist-icon', WishlistIcon);
}
if (!customElements.get('wishlist-toggle')) {
  customElements.define('wishlist-toggle', WishlistToggle);
}
